import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
  Tooltip,
} from "@heroui/react";
import { Link } from "react-router-dom";
import { BuildingDTO } from "shared/types/building";
import {
  ExclusionType,
  ImagesContainer,
} from "shared/components/imagesContainer/imagesContainer";
import { FileLabel } from "shared/types/file";
import { applyMask } from "shared/utils/masks";
import {
  BuildingApartment,
  CarProfile,
  Info,
  StackIcon,
  Certificate,
  Heart,
} from "@phosphor-icons/react";
import StatusTooltip from "screens/buildings/components/statusTab/StatusTooltip";
import { useBuildingDueDiligence } from "shared/hooks/useBuildingDueDiligence";
import { HelpdeskTicketsInline } from "screens/buildings/components/HelpdeskTicketsInline";

interface BuildingCardProps {
  building: BuildingDTO;
  isLoadingBuildingSummaries?: boolean;
}

// Helper to create tooltip content (can stay outside the component)
export const createTooltipContent = (sources: any) => {
  if (!sources || sources.length === 0) return [];

  // Group by category first
  const categoryGroups = sources.reduce((acc: any, source: any) => {
    const [category, ...rest] = source.label.split(':');
    const statusText = rest.join(':').trim();

    if (!acc[category]) acc[category] = [];

    // Add to the category with the source details
    acc[category].push({
      ...source,
      statusText: statusText || source.label // Use full label if splitting failed
    });

    return acc;
  }, {});

  // Process the groups to create summary information
  return Object.entries(categoryGroups).map(([category, items]: any) => {
    // Get building items 
    const buildingItems = items.filter((item: any) => item.isBuilding);

    // Group unit items by status text
    const unitItemsByStatus = items
      .filter((item: any) => item.isUnit)
      .reduce((acc: any, item: any) => {
        if (!acc[item.statusText]) acc[item.statusText] = [];
        acc[item.statusText].push(item);
        return acc;
      }, {});

    return {
      category,
      buildingItems,
      unitItemsByStatus
    };
  });
};

export const BuildingCard = ({ building }: BuildingCardProps) => {
  // Use the new optimized hook
  const {
    dotValues: numericValues,
    isLoading: isLoadingDueDiligence,
    isRevalidating,
  } = useBuildingDueDiligence(building, {
    includeUnits: true,
    allowStale: true,
  });

  return (
    <>
      {building && (
        <Link to={`/building/${building.id}`} key={building.id}>
          <Card className="flex gap-3 rounded-lg bg-muted p-4 shadow-md will-change-transform hover:scale-[1.0124]">
            <CardHeader className="flex-col items-start gap-1.5 rounded-none p-0">
              <div className="flex w-full items-center justify-between">
                <p className="text-sm font-medium uppercase text-neutral-secondary-mid">
                  {building.id}
                </p>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <HelpdeskTicketsInline building={building} />
                  <Chip
                    className="gap-1"
                    startContent={<StackIcon weight="duotone" size={14} />}
                    classNames={{
                      base: "h-fit bg-muted px-2 border border-muted-foreground/50 text-muted-foreground",
                      content: "px-0 font-medium text-muted-foreground",
                    }}
                  >
                    {typeof building.units === 'number' ? building.units : (building.units?.length || 0)} units
                  </Chip>
                </div>
              </div>
            </CardHeader>
            <section className="flex flex-col items-end gap-3 sm:grid sm:grid-cols-2">
              <ImagesContainer
                pageSize={1}
                canUpload={false}
                variant="light"
                display="inline"
                excludes={[ExclusionType.Unit]}
                fileLabel={FileLabel.CoverImage}
                buildingId={building && building.id && (building as any).id}
              />
              <CardBody className="flex h-full flex-col justify-between gap-3 p-0 sm:col-span-1">
                <div className="flex cursor-pointer flex-wrap items-center justify-center gap-2 rounded-lg border border-neutral-primary/10 p-2 relative">
                  {isLoadingDueDiligence || !numericValues ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span className="text-sm text-neutral-secondary-mid">
                        Due diligence status berekenen...
                      </span>
                    </div>
                  ) : (
                    <>
                      {isRevalidating && (
                        <div className="absolute top-1 right-1">
                          <Spinner size="sm" color="secondary" />
                        </div>
                      )}
                      <StatusTooltip
                        numericValues={numericValues}
                        subTitle={building.id}
                        color="green"
                      />
                      <StatusTooltip
                        numericValues={numericValues}
                        subTitle={building.id}
                        color="orange"
                      />
                      <StatusTooltip
                        numericValues={numericValues}
                        subTitle={building.id}
                        color="red"
                      />
                      <StatusTooltip
                        numericValues={numericValues}
                        subTitle={building.id}
                        color="transparent"
                      />
                    </>
                  )}
                </div>
                <section className="flex flex-col gap-3">
                  <div className="flex flex-col gap-0.5">
                    <p className="text-lg">
                      {applyMask(
                        building?.totalCashOutIncludingRenovation ||
                        ((building?.buildingAcquisitionInfo as any)?.totalCashOutIncludingRenovation),
                        "valuta",
                      )}
                    </p>
                    <p className="mb-4 text-sm font-medium">
                      {building.googlePlaceLongAddress}
                    </p>
                    <div className="group-hover mb-4 flex items-center justify-between rounded-md border border-neutral-primary/20 p-2 text-sm duration-300 ease-in-out hover:border-moss-primary/20 hover:bg-moss-primary/10">
                      <div>
                        <div className="flex gap-2">
                          Geïndexeerde contractuele bruto huur/jaar (excl.
                          provisies):
                          <Tooltip content="De bruto huur is de totale huurinkomsten per jaar, excl. doorrekenbare provisies en OV en vóór aftrek van Operating Expenses">
                            <Info />
                          </Tooltip>
                        </div>

                        {building?.grossActualRentalIncomePerYear ? (
                          <span className="font-medium">
                            {applyMask(
                              building.grossActualRentalIncomePerYear,
                              "valuta",
                            )}
                          </span>
                        ) : (
                          <div className="flex gap-2">
                            <Spinner color="warning" size="sm" />
                            <span className="font-medium">
                              Aan het berekenen...
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <ul className="flex flex-col gap-1.5">
                    {building.buildingType && (
                      <li className="flex items-center gap-1 text-sm text-neutral-secondary-mid">
                        <Info weight="duotone" size={16} />
                        {building.buildingType}
                      </li>
                    )}
                    {building.company && (
                      <li className="flex items-center gap-1 text-sm text-neutral-secondary-mid">
                        <BuildingApartment weight="duotone" size={16} />
                        {building.company.name}
                      </li>
                    )}
                    {building.mobiscore && (
                      <li className="flex items-center gap-1 text-sm text-neutral-secondary-mid">
                        <CarProfile weight="duotone" size={16} />
                        Mobiscore{" "}
                        <b className="text-neutral-primary">
                          {building.mobiscore}
                        </b>
                      </li>
                    )}
                    {building.breeamRating && (
                      <li className="flex items-center gap-1 text-sm text-neutral-secondary-mid">
                        <Certificate weight="duotone" size={16} />
                        BREEAM{" "}
                        <b className="text-moss-primary">
                          {building.breeamRating}
                        </b>
                      </li>
                    )}
                    {building.wellRating && (
                      <li className="flex items-center gap-1 text-sm text-neutral-secondary-mid">
                        <Heart weight="duotone" size={16} />
                        WELL{" "}
                        <b className="text-brick-primary">
                          {building.wellRating}
                        </b>
                      </li>
                    )}
                  </ul>
                </section>
              </CardBody>
            </section>
          </Card>
        </Link>
      )}
    </>
  );
};
